const NOTIFICATION_PREFERENCES_STORAGE_KEY = "wifiads-notification-preferences";
const LANGUAGE_STORAGE_KEY = "wifiads-language";
const TIME_ZONE_STORAGE_KEY = "wifiads-time-zone";
const SUPPORTED_LANGUAGES = ["es", "en"];

export const DEFAULT_NOTIFICATION_PREFERENCES = {
  categories: {
    accountAlerts: {
      email: true,
      browser: true,
      whatsapp: false,
    },
    campaignUpdates: {
      email: true,
      browser: false,
      whatsapp: false,
    },
    financialEvents: {
      email: true,
      browser: true,
      whatsapp: false,
    },
    technicalStatus: {
      email: false,
      browser: true,
      whatsapp: false,
    },
  },
  language: "es",
  deliveryFrequency: "instant",
  timeZone: "America/Santo_Domingo",
  savedAt: null,
};

export const LANGUAGE_OPTIONS = [
  {
    id: "es",
    label: "Español",
    helper: "Idioma predeterminado del sistema para usuarios de habla hispana.",
  },
  {
    id: "en",
    label: "English",
    helper: "Useful for users who prefer dashboards and navigation in English.",
  },
];

export const TIME_ZONE_OPTIONS = [
  {
    id: "America/Santo_Domingo",
    label: "AST - Atlantic Standard Time (UTC-4)",
    helper: "República Dominicana, Puerto Rico y parte del Caribe.",
  },
  {
    id: "America/New_York",
    label: "ET - Eastern Time (UTC-5/UTC-4)",
    helper: "Costa este de Estados Unidos.",
  },
  {
    id: "America/Chicago",
    label: "CT - Central Time (UTC-6/UTC-5)",
    helper: "Centro de Estados Unidos y parte de México.",
  },
  {
    id: "America/Denver",
    label: "MT - Mountain Time (UTC-7/UTC-6)",
    helper: "Zona montañosa de Estados Unidos.",
  },
  {
    id: "America/Los_Angeles",
    label: "PT - Pacific Time (UTC-8/UTC-7)",
    helper: "Costa oeste de Estados Unidos.",
  },
  {
    id: "UTC",
    label: "UTC - Coordinated Universal Time",
    helper: "Referencia global sin desplazamiento local.",
  },
];

export function buildNotificationPreferenceStorageKey(email) {
  return `${NOTIFICATION_PREFERENCES_STORAGE_KEY}:${email || "default"}`;
}

export function normalizeNotificationPreferences(nextPreferences) {
  const safePreferences =
    nextPreferences &&
    typeof nextPreferences === "object" &&
    !Array.isArray(nextPreferences)
      ? nextPreferences
      : {};

  const categories = Object.keys(
    DEFAULT_NOTIFICATION_PREFERENCES.categories
  ).reduce((accumulator, categoryId) => {
    accumulator[categoryId] = {
      ...DEFAULT_NOTIFICATION_PREFERENCES.categories[categoryId],
      ...(safePreferences.categories?.[categoryId] || {}),
    };

    return accumulator;
  }, {});

  return {
    ...DEFAULT_NOTIFICATION_PREFERENCES,
    ...safePreferences,
    categories,
    language:
      safePreferences.language || DEFAULT_NOTIFICATION_PREFERENCES.language,
    timeZone:
      safePreferences.timeZone || DEFAULT_NOTIFICATION_PREFERENCES.timeZone,
  };
}

export function loadNotificationPreferences(email) {
  try {
    const storedPreferences = localStorage.getItem(
      buildNotificationPreferenceStorageKey(email)
    );

    if (!storedPreferences) {
      return normalizeNotificationPreferences();
    }

    return normalizeNotificationPreferences(JSON.parse(storedPreferences));
  } catch (error) {
    return normalizeNotificationPreferences();
  }
}

export function persistNotificationPreferences(email, nextPreferences) {
  localStorage.setItem(
    buildNotificationPreferenceStorageKey(email),
    JSON.stringify(nextPreferences)
  );
  if (SUPPORTED_LANGUAGES.includes(nextPreferences?.language)) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, nextPreferences.language);
  }
  if (nextPreferences?.timeZone) {
    localStorage.setItem(TIME_ZONE_STORAGE_KEY, nextPreferences.timeZone);
  }
}

export function getPreferredTimeZone() {
  try {
    const storedSession = JSON.parse(
      sessionStorage.getItem("token_store") || "null"
    );
    const email = storedSession?.email;
    return (
      localStorage.getItem(TIME_ZONE_STORAGE_KEY) ||
      (email ? loadNotificationPreferences(email).timeZone : null) ||
      DEFAULT_NOTIFICATION_PREFERENCES.timeZone
    );
  } catch (error) {
    return DEFAULT_NOTIFICATION_PREFERENCES.timeZone;
  }
}

export function persistPreferredTimeZone(timeZone) {
  const normalizedTimeZone =
    timeZone || DEFAULT_NOTIFICATION_PREFERENCES.timeZone;
  localStorage.setItem(TIME_ZONE_STORAGE_KEY, normalizedTimeZone);
  return normalizedTimeZone;
}

export function getPreferredLanguage() {
  try {
    const storedSession = JSON.parse(
      sessionStorage.getItem("token_store") || "null"
    );
    const email = storedSession?.email;
    const profileLanguage = loadNotificationPreferences(email).language;
    const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    const language = storedLanguage || (email ? profileLanguage : null);
    return SUPPORTED_LANGUAGES.includes(language) ? language : null;
  } catch (error) {
    return null;
  }
}

export function persistPreferredLanguage(language) {
  const normalizedLanguage = SUPPORTED_LANGUAGES.includes(language)
    ? language
    : DEFAULT_NOTIFICATION_PREFERENCES.language;

  localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizedLanguage);

  try {
    const storedSession = JSON.parse(
      sessionStorage.getItem("token_store") || "null"
    );
    const email = storedSession?.email;
    if (email) {
      const preferences = loadNotificationPreferences(email);
      persistNotificationPreferences(email, {
        ...preferences,
        language: normalizedLanguage,
      });
    }
  } catch (error) {
    // The global preference remains available for anonymous sessions.
  }

  return normalizedLanguage;
}

function parseAnalyticsDateTime(value) {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === "number") {
    return new Date(value);
  }

  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const trimmedValue = value.trim();
  const normalizedValue = trimmedValue.includes("T")
    ? trimmedValue
    : trimmedValue.replace(" ", "T");
  const utcCandidate = /Z$|[+-]\d\d:?\d\d$/.test(normalizedValue)
    ? normalizedValue
    : `${normalizedValue}Z`;

  const parsedUtcDate = new Date(utcCandidate);
  if (!Number.isNaN(parsedUtcDate.getTime())) {
    return parsedUtcDate;
  }

  const parsedLocalDate = new Date(normalizedValue);
  if (!Number.isNaN(parsedLocalDate.getTime())) {
    return parsedLocalDate;
  }

  return null;
}

export function formatAnalyticsDateTime(value, options = {}) {
  const parsedDate = parseAnalyticsDateTime(value);

  if (!parsedDate) {
    return String(value || "");
  }

  const {
    timeZone = getPreferredTimeZone(),
    dateStyle = "short",
    timeStyle = "short",
  } = options;

  const locale = getPreferredLanguage() === "en" ? "en-US" : "es-DO";

  return new Intl.DateTimeFormat(locale, {
    timeZone,
    dateStyle,
    timeStyle,
  }).format(parsedDate);
}

export function formatAnalyticsHourLabel(value, options = {}) {
  const parsedDate = parseAnalyticsDateTime(value);

  if (!parsedDate) {
    return String(value || "");
  }

  const { timeZone = getPreferredTimeZone() } = options;

  const locale = getPreferredLanguage() === "en" ? "en-US" : "es-DO";

  return new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  }).format(parsedDate);
}

export function mapAnalyticsDateTimes(items, fieldName = "dateTime") {
  return (items || []).map((item) =>
    formatAnalyticsDateTime(item?.[fieldName], {
      dateStyle: "short",
      timeStyle: "short",
    })
  );
}
